import {useMemo} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {D20Page} from '@/pages/components/audit-program/d-section/constants';
import {D20_PAGE} from '@/pages/components/audit-program/d-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface D20BlankSummaryViewProps {
    header: AuditProgramSectionVO;
    page: D20Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.35, lineHeight: 1.25};
const bodySx = {...cellBorder, py: 0.4, px: 0.35, verticalAlign: 'top' as const};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** D20 — Blank summary sheet template. */
export default function D20BlankSummaryView({
    header,
    page,
    refCode = 'D20',
    values,
    onChange,
}: D20BlankSummaryViewProps) {
    const meta = {
        title: page.title || D20_PAGE.title,
        planningNote: page.planningNote || D20_PAGE.planningNote,
        finalBullets: page.finalBullets?.length ? page.finalBullets : D20_PAGE.finalBullets,
        blankObjectiveRows: page.blankObjectiveRows || D20_PAGE.blankObjectiveRows,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankObjectiveRows)}, (_, i) => `obj${i + 1}`),
        [meta.blankObjectiveRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1}}>
                Audit objectives
            </Typography>
            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 900, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} style={{width: 36}}/>
                            <TableCell sx={headSx}>Objective</TableCell>
                            <TableCell sx={headSx} align="center">Analytical review</TableCell>
                            <TableCell sx={headSx} align="center">Tests of control</TableCell>
                            <TableCell sx={headSx} align="center">Substantive tests</TableCell>
                            <TableCell sx={headSx} align="center">Objectives met?</TableCell>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2">{idx + 1}</Typography>
                                </TableCell>
                                {(['text', 'analytical', 'controls', 'substantive', 'met', 'comments'] as const).map((field) => (
                                    <TableCell key={field} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align={field === 'text' || field === 'comments' ? 'left' : 'center'}
                                            placeholder={field === 'met' ? 'Yes/No' : undefined}
                                            sx={fieldSx}
                                            value={values[`${id}.${field}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${field}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                Planning conclusion
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {meta.planningNote}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                Final conclusion
            </Typography>
            <Typography variant="body2" sx={{mb: 0.5}}>
                From the audit work carried out I confirm that:
            </Typography>
            <Stack spacing={0.25} sx={{mb: 1.5, pl: 1}}>
                {meta.finalBullets.map((b, idx) => (
                    <Typography key={idx} variant="body2">- {b}</Typography>
                ))}
            </Stack>
            <InputField
                type="text"
                fullWidth
                compact
                multiline
                minRows={2}
                placeholder="Additional comments"
                value={values.finalComments ?? ''}
                onChange={(e) => onChange('finalComments', e.target.value)}
                sx={{maxWidth: 720}}
            />
        </Box>
    );
}
